// SMTP Box Health Dashboard — pool overview with box cards

import { Server } from 'lucide-react'
import { useState } from 'react'
import { BoxCard } from '@/components/boxes/box-card'
import { BoxDetailPanel } from '@/components/boxes/box-detail-panel'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { Modal } from '@/components/ui/modal'
import {
  useBoxes,
  usePauseBox,
  usePoolStatus,
  useResumeBox,
  useUpdateBoxWeight,
} from '@/hooks/use-boxes'
import type { PoolSummary, SmtpBox } from '@/lib/types-boxes'

function formatRate(rate: string | number): string {
  const num = typeof rate === 'string' ? Number.parseFloat(rate) : rate
  return `${(num * 100).toFixed(2)}%`
}

function PoolSection({
  title,
  pool,
  boxes,
  onSelect,
  onPause,
  onResume,
  onAdjustWeight,
  pausingId,
  resumingId,
}: {
  title: string
  pool: PoolSummary | undefined
  boxes: SmtpBox[]
  onSelect: (id: string) => void
  onPause: (id: string) => void
  onResume: (id: string) => void
  onAdjustWeight: (id: string) => void
  pausingId: string | null
  resumingId: string | null
}): React.JSX.Element {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        {pool && (
          <div className="flex gap-4 text-sm text-text-secondary">
            <span>
              Health: <strong>{pool.avg_health}/100</strong>
            </span>
            <span>
              Volume: <strong>{pool.total_volume.toLocaleString()}/day</strong>
            </span>
            <span>
              Bounce: <strong>{formatRate(pool.avg_bounce_rate)}</strong>
            </span>
          </div>
        )}
      </div>
      {boxes.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-3 p-6 text-center text-sm text-text-tertiary">
          No boxes in this pool
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {boxes.map((box) => (
            <BoxCard
              key={box.id}
              box={box}
              onSelect={onSelect}
              onPause={onPause}
              onResume={onResume}
              onAdjustWeight={onAdjustWeight}
              isPausing={pausingId === box.id}
              isResuming={resumingId === box.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function BoxesPage(): React.JSX.Element {
  const boxesQuery = useBoxes()
  const poolQuery = usePoolStatus()
  const pauseMutation = usePauseBox()
  const resumeMutation = useResumeBox()
  const weightMutation = useUpdateBoxWeight()

  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null)
  const [weightModalBoxId, setWeightModalBoxId] = useState<string | null>(null)
  const [newWeight, setNewWeight] = useState('')

  const boxes = boxesQuery.data?.boxes ?? []
  const pools = poolQuery.data?.pools ?? []

  const mktBoxes = boxes.filter((b) => b.pool === 'marketing')
  const txnBoxes = boxes.filter((b) => b.pool === 'transactional')
  const mktPool = pools.find((p) => p.pool === 'marketing')
  const txnPool = pools.find((p) => p.pool === 'transactional')

  const pausingId = pauseMutation.isPending ? (pauseMutation.variables as string) : null
  const resumingId = resumeMutation.isPending ? (resumeMutation.variables as string) : null

  function handleAdjustWeight(boxId: string): void {
    const box = boxes.find((b) => b.id === boxId)
    setNewWeight(String(box?.weight ?? 0))
    setWeightModalBoxId(boxId)
  }

  function submitWeight(): void {
    if (!weightModalBoxId) return
    const w = Number.parseInt(newWeight, 10)
    if (Number.isNaN(w) || w < 0 || w > 100) return
    weightMutation.mutate({ id: weightModalBoxId, weight: w })
    setWeightModalBoxId(null)
  }

  if (boxesQuery.isLoading) {
    return <LoadingPage message="Loading boxes..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Server className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">SMTP Boxes</h1>
      </div>

      {/* Pool summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Total Boxes" value={String(boxes.length)} />
        <MetricCard
          label="Avg Health"
          value={
            pools.length > 0
              ? `${Math.round(pools.reduce((s, p) => s + p.avg_health, 0) / pools.length)}/100`
              : 'N/A'
          }
        />
        <MetricCard
          label="Total Volume"
          value={pools.reduce((s, p) => s + p.total_volume, 0).toLocaleString()}
          sub="emails/day"
        />
        <MetricCard
          label="Active Boxes"
          value={String(
            boxes.filter((b) => !['paused', 'decommissioned'].includes(b.status)).length,
          )}
        />
      </div>

      {/* Marketing pool */}
      <PoolSection
        title="Marketing Pool"
        pool={mktPool}
        boxes={mktBoxes}
        onSelect={setSelectedBoxId}
        onPause={(id) => pauseMutation.mutate(id)}
        onResume={(id) => resumeMutation.mutate(id)}
        onAdjustWeight={handleAdjustWeight}
        pausingId={pausingId}
        resumingId={resumingId}
      />

      {/* Transactional pool */}
      <PoolSection
        title="Transactional Pool"
        pool={txnPool}
        boxes={txnBoxes}
        onSelect={setSelectedBoxId}
        onPause={(id) => pauseMutation.mutate(id)}
        onResume={(id) => resumeMutation.mutate(id)}
        onAdjustWeight={handleAdjustWeight}
        pausingId={pausingId}
        resumingId={resumingId}
      />

      {/* Detail panel */}
      {selectedBoxId && (
        <BoxDetailPanel boxId={selectedBoxId} onClose={() => setSelectedBoxId(null)} />
      )}

      {/* Weight adjustment modal */}
      <Modal
        open={!!weightModalBoxId}
        onClose={() => setWeightModalBoxId(null)}
        title="Adjust Weight"
        size="sm"
      >
        <div className="space-y-4">
          <label className="block text-sm text-text-secondary">
            New weight (0-100)
            <input
              type="number"
              min={0}
              max={100}
              value={newWeight}
              onChange={(e) => setNewWeight(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary focus:border-admin-500 focus:outline-none"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setWeightModalBoxId(null)}
              className="rounded-lg px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitWeight}
              disabled={weightMutation.isPending}
              className="rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
            >
              {weightMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
